import { useCallback, useState } from 'react';

import { SelfServiceSMBSignupModal } from '@/auth/components/SelfServiceSMBSignupModal';
import { useSelfServiceSMBSignupModal } from '@/auth/components/SelfServiceSMBSignupModal/hooks/useSelfServiceSMBSignupModal';

import { SetupPausedBanner } from './SetupPausedBanner';

interface Props {
  firstName?: string | null;
  email?: string | null;
}

// Sub-component so the self-service signup hook (which runs viewer queries,
// instantiates mutations, and parses URL trial params on every render) is
// only mounted on the SMB-without-billing branch. Mounting it at dashboard
// top-level made experts, partner customers, etc. pay for work they never
// triggered.
//
// skipHasTeamBillingRedirect: the dashboard is a valid destination after
// signup completes — we don't want the hook's "viewer already has billing"
// guard to bounce a fully-signed-up user away from the page they're
// currently looking at.
export function SetupPausedBannerSurface({ firstName, email }: Props): JSX.Element {
  const [signupOpen, setSignupOpen] = useState(false);
  const handleOpenSignup = useCallback(() => setSignupOpen(true), []);
  const handleCloseSignup = useCallback(() => setSignupOpen(false), []);
  // ENG-5251 (PR #2688 review C2): pass onClose so the modal's Dismiss CTA
  // (the dashboard branch sets dismissInsteadOfDashboard=true / ctaLabel='Dismiss')
  // can actually close the modal. Without onClose, `onGoToDashboard`'s
  // short-circuit fails the truthiness check and the click does nothing
  // visible. Every other caller of this hook (AccessHeader,
  // TeamAccountPromoButton) already passes onClose.
  const { modalProps } = useSelfServiceSMBSignupModal({
    skipHasTeamBillingRedirect: true,
    onClose: handleCloseSignup,
  });
  return (
    <>
      <SetupPausedBanner firstName={firstName} email={email} onContinueSetup={handleOpenSignup} />
      <SelfServiceSMBSignupModal {...modalProps} open={signupOpen} onClose={handleCloseSignup} />
    </>
  );
}
